<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import {
  useMapGetter,
} from 'dashboard/composables/store.js';

const currentUser = useMapGetter('getCurrentUser');

const props = defineProps({
  agentData: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  agents: {
    type: Array,
    default: () => [],
  },
});

// 生成小时数组 0-23
const hours = Array.from({ length: 24 }, (_, i) => i);

/**
 * 将 agentData 归一化为每个 agent 24 小时都存在的 entries，没数据的小时 minutes 为 0
 * 根据 window.VENDOR_AGENT_IDS 进行过滤
 */
const normalizedAgentData = computed(() => {
  let filteredAgentData = props.agentData ?? [];
  
  // 解析 window.hpVendorTLIds 字符串为数组
  try {
    const hpVendorTLIds = window.urConfig?.hpVendorTLIds ? JSON.parse(window.urConfig.hpVendorTLIds) : [];
    
    // 检查当前用户ID是否在 hpVendorTLIds 中
    if (Array.isArray(hpVendorTLIds) && hpVendorTLIds.includes(currentUser.value.id)) {
      // 解析 window.hpVendorAgentIds 字符串为数组
      const hpVendorAgentIds = window.urConfig?.hpVendorAgentIds ? JSON.parse(window.urConfig.hpVendorAgentIds) : [];
      
      // 根据 hpVendorAgentIds 过滤 agent 数据
      if (Array.isArray(hpVendorAgentIds) && hpVendorAgentIds.length > 0) {
        filteredAgentData = filteredAgentData.filter(agent => 
          hpVendorAgentIds.includes(agent.userId)
        );
      }
    }
  } catch (error) {
    console.warn('解析 vendor 配置失败:', error);
  }

  return filteredAgentData.map(agent => {
    // 以小时为 key 方便查找
    const hourMap = Object.fromEntries(
      (agent.entries || []).map(entry => [entry.hour, entry.minutes])
    );
    return {
      name: agent.name,
      entries: hours.map(hour => ({
        hour,
        minutes: hourMap[hour] || 0,
      })),
      id: agent.userId
    };
  });
});

// 获取格子的透明度
function getCellOpacity(minutes) {
  if (!minutes) return 0;
  return Math.max(0.1, minutes / 60); // 最低0.1，避免完全不可见
}

// 添加响应式的暗色模式状态
const isDark = ref(false);

// 检查暗色模式状态
function checkDarkMode() {
  isDark.value = document.body.classList.contains('dark');
}

// 监听主题变化
const observer = ref(null);

onMounted(() => {
  checkDarkMode();
  
  // 使用 MutationObserver 监听 body 类名变化
  observer.value = new MutationObserver(() => {
    checkDarkMode();
  });
  
  observer.value.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });
});

onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect();
  }
});

// 获取格子的背景色，透明度为0时兜底灰色
function getCellBackground(minutes) {
  const opacity = getCellOpacity(minutes);
  if (opacity === 0) {
    return { backgroundColor: isDark.value ? 'rgb(90 97 105 / 0.4)' : 'rgba(240,240,243,0.5)' };
  }
  return { backgroundColor: `rgba(0,179,110,${opacity})` };
}

const agentNameCol = ref(null);
const entriesCol = ref(null);
let isSyncing = false;

/**
 * 同步左右两列的滚动
 */
function handleScroll(source) {
  if (isSyncing) return;
  isSyncing = true;
  if (source === 'name') {
    if (entriesCol.value) entriesCol.value.scrollTop = agentNameCol.value.scrollTop;
  } else {
    if (agentNameCol.value) agentNameCol.value.scrollTop = entriesCol.value.scrollTop;
  }
  isSyncing = false;
}

/**
 * 获取 agent 的状态
 */
function getAgentStatus(userId) {
  const found = props.agents.find(a => a.id === userId);
  return found ? found.availability_status : 'offline';
}

/**
 * 根据状态返回圆点颜色
 */
function getStatusColor(status) {
  switch (status) {
    case 'online': return 'rgb(51,176,116)';
    case 'busy': return 'rgb(245,225,71)';
    case 'offline':
    default: return 'rgb(189,189,189)';
  }
}
</script>

<template>
  <div
    class="grid relative w-full gap-x-4 gap-y-2.5 overflow-y-scroll md:overflow-visible grid-cols-[80px_1fr]"
  >
    <template v-if="isLoading">
      <div class="grid gap-[5px] flex-shrink-0">
        <div
          v-for="ii in 7"
          :key="ii"
          class="w-full rounded-sm bg-slate-100 dark:bg-slate-900 animate-loader-pulse h-8 min-w-[70px]"
        />
      </div>
      <div class="grid gap-[5px] w-full min-w-[700px]">
        <div
          v-for="ii in 7"
          :key="ii"
          class="grid gap-[5px] grid-cols-[repeat(24,_1fr)]"
        >
          <div
            v-for="jj in 24"
            :key="jj"
            class="w-full h-8 rounded-sm bg-slate-100 dark:bg-slate-900 animate-loader-pulse"
          />
        </div>
      </div>
      <div />
      <div
        class="grid grid-cols-[repeat(24,_1fr)] gap-[5px] w-full text-[8px] font-semibold h-5 text-slate-800 dark:text-slate-200"
      >
        <div
          v-for="ii in 24"
          :key="ii"
          class="flex items-center justify-center"
        >
          {{ ii - 1 }} – {{ ii }}
        </div>
      </div>
    </template>
    <template v-else>
      <div
        class="grid relative w-full gap-x-4 gap-y-2.5 overflow-y-scroll md:overflow-visible grid-cols-[80px_1fr]"
      >
        <div
          ref="agentNameCol"
          class="grid gap-[5px] flex-shrink-0 max-h-[380px] overflow-y-auto scrollbar-hide"
          @scroll="handleScroll('name')"
        >
          <div
            v-for="agent in normalizedAgentData"
            :key="agent.name"
            class="h-8 min-w-[70px] text-slate-800 dark:text-slate-200 text-[10px] font-semibold flex items-center"
          >
            <span
              class="inline-block w-2 h-2 rounded-full mr-2 align-middle flex-shrink-0"
              :style="{ backgroundColor: getStatusColor(getAgentStatus(agent.id)) }"
            ></span>
            {{ agent.name }}
          </div>
        </div>
        <div
          ref="entriesCol"
          class="grid gap-[5px] w-full min-w-[810px] max-h-[380px] overflow-y-auto scrollbar-hide"
          @scroll="handleScroll('entries')"
        >
          <div
            v-for="agent in normalizedAgentData"
            :key="agent.name"
            class="grid gap-[5px] grid-cols-[repeat(24,_1fr)]"
          >
            <div
              v-for="cell in agent.entries"
              :key="cell.hour"
              v-tooltip.top="`${agent.name} ${cell.hour}:00–${cell.hour + 1}:00 Online: ${cell.minutes} min`"
              class="h-8 rounded-sm shadow-inner shadow-black transition-opacity duration-200"
              :style="getCellBackground(cell.minutes)"
            />
          </div>
        </div>
        <div />
        <div
          class="grid grid-cols-[repeat(24,_1fr)] gap-[5px] w-full text-[8px] font-semibold h-5 text-slate-800 dark:text-slate-200"
        >
          <div
            v-for="ii in 24"
            :key="ii"
            class="flex items-center justify-center"
          >
            {{ ii - 1 }} – {{ ii }}
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
/* 隐藏滚动条但保留滚动功能 */
.scrollbar-hide {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
}
.scrollbar-hide::-webkit-scrollbar {
  display: none; /* Chrome/Safari/Webkit */
}
</style> 